import { useEffect, useState } from "react";
import useHttp from "./use_http";
import { useFormik } from "formik";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import {
  setUserProfile,
  setUserProfileLoading,
} from "../features/app/app_slice";
import {
  addSocialMediaSchema,
  updatePasswordSchema,
  updateSettingSchema,
} from "../utils/schema";

const useSetting = () => {
  const dispatch = useDispatch();
  const { httpService } = useHttp();

  const [loadings, setLoadings] = useState({
    updateAvatar: false,
    updateSetting: false,
    updatePassword: false,
    addSocialMedia: false,
    updateSocialMedia: false,
    deleteSocialMedia: false,
  });
  const [getCountriesLoading, setGetCountriesLoading] = useState(false);
  const [getCategoriesLoading, setGetCategoriesLoading] = useState(false);

  const [newAccountModal, setNewAccountModal] = useState(false);
  const [updateAccountModal, setUpdateAccountModal] = useState(false);
  const [deleteAccountModal, setDeleteAccountModal] = useState(false);
  const [selectedSocialMedia, setSelectedSocialMedia] = useState(null);

  const [countries, setCountries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [socialMediaAccounts, setSocialMediaAccounts] = useState([]);

  const userProfile = useSelector((state) => state.app.userProfile);

  const getUserProfile = async () => {
    try {
      dispatch(setUserProfileLoading(true));
      const response = await httpService.get("user/profile");
      dispatch(setUserProfileLoading(false));
      dispatch(setUserProfile(response.data));
    } catch ({ err, response }) {
      dispatch(setUserProfileLoading(false));
    }
  };

  const getCountries = async () => {
    try {
      setGetCountriesLoading(true);
      const response = await httpService.get("country");
      setGetCountriesLoading(false);
      setCountries(response.data);
    } catch ({ err, response }) {
      setGetCountriesLoading(false);
    }
  };

  const getCategories = async () => {
    let array = [];
    try {
      setGetCategoriesLoading(true);
      const response = await httpService.get("category");
      setGetCategoriesLoading(false);
      response.data.map((item) => {
        array.push({
          label: item.translations.en?.name,
          value: item.id,
        });
      });
      setCategories(array);
    } catch ({ err, response }) {
      setGetCategoriesLoading(false);
    }
  };

  const updateAvatar = async (file) => {
    let postData = new FormData();
    postData.append("avatar", file);
    try {
      setLoadings({ ...loadings, updateAvatar: true });
      const response = await httpService.post("user/avatar", postData);
      setLoadings({ ...loadings, updateAvatar: false });
      if (response.status < 400) {
        toast.success(response.data.message);
        getUserProfile();
      }
    } catch ({ err, response }) {
      setLoadings({ ...loadings, updateAvatar: false });
    }
  };

  const updateSettingController = useFormik({
    initialValues: {
      fullname: "",
      countryId: null,
      expertises: [],
      bio: "",
      mobile: "",
      email: "",
      gender: null,
      address: "",
    },
    validationSchema: updateSettingSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      updateSetting(values);
    },
  });

  const updateSetting = async (values) => {
    let postData = {
      fullname: values.fullname,
      countryId: values.countryId,
      expertises: values.expertises,
      bio: values.bio,
      mobile: values.mobile,
      email: values.email,
      gender: values.gender,
      address: values.address,
    };
    try {
      setLoadings({ ...loadings, updateSetting: true });
      const response = await httpService.put("user", postData);
      setLoadings({ ...loadings, updateSetting: false });
      if (response.status < 400) {
        toast.success(response.data.message);
        getUserProfile();
      }
    } catch ({ err, response }) {
      setLoadings({ ...loadings, updateSetting: false });
    }
  };

  const updatePasswordController = useFormik({
    initialValues: {
      currentPassword: "",
      newPassword: "",
      newPasswordConfirm: "",
    },
    validationSchema: updatePasswordSchema,
    onSubmit: (values) => {
      if (values.newPassword !== values.newPasswordConfirm) {
        toast.error("New password and new password confirmation isn't equal.");
      } else {
        updatePassword(values);
      }
    },
  });

  const updatePassword = async (values) => {
    try {
      setLoadings({ ...loadings, updatePassword: true });
      const response = await httpService.put("user/password", {
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      setLoadings({ ...loadings, updatePassword: false });
      if (response.status < 400) {
        toast.success(response.data.message);
        updatePasswordController.resetForm();
      }
    } catch ({ err, response }) {
      setLoadings({ ...loadings, updatePassword: false });
    }
  };

  const addSocialMediaController = useFormik({
    initialValues: {
      type: null,
      link: "",
    },
    validationSchema: addSocialMediaSchema,
    onSubmit: (values) => {
      addSocialMedia(values);
    },
  });

  const addSocialMedia = async (values) => {
    let array = [...socialMediaAccounts];
    array.push({
      type: values.type,
      link: values.link,
    });
    let postData = {
      fullname: userProfile.fullname,
      countryId: userProfile?.country?.id,
      expertises: userProfile?.instructor?.expertises?.map((item) => {
        return item.id;
      }),
      bio: userProfile?.instructor?.bio,
      mobile: userProfile?.mobile,
      email: userProfile?.email,
      gender: userProfile.gender,
      address: userProfile?.address,
      meta: {
        social_media_links: array,
      },
    };
    try {
      setLoadings({ ...loadings, addSocialMedia: true });
      const response = await httpService.put("user", postData);
      setLoadings({ ...loadings, addSocialMedia: false });
      if (response.status < 400) {
        setNewAccountModal(false);
        toast.success("Added successfully");
        setSocialMediaAccounts(array);
      }
    } catch ({ err, response }) {
      setLoadings({ ...loadings, addSocialMedia: false });
    }
  };

  const updateSocialMediaController = useFormik({
    initialValues: {
      type: null,
      link: "",
    },
    validationSchema: addSocialMediaSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      updateSocialMedia(values);
    },
  });

  const updateSocialMedia = async (values) => {
    const array = socialMediaAccounts.map((item) => {
      if (item.type === values.type) {
        return {
          ...item,
          link: values.link,
        };
      }
      return item;
    });
    const postData = {
      fullname: userProfile.fullname,
      countryId: userProfile?.country?.id,
      expertises: userProfile?.instructor?.expertises?.map((item) => item.id),
      bio: userProfile?.instructor?.bio,
      mobile: userProfile?.mobile,
      email: userProfile?.email,
      gender: userProfile.gender,
      address: userProfile?.address,
      meta: {
        social_media_links: array,
      },
    };
    try {
      setLoadings({ ...loadings, updateSocialMedia: true });
      const response = await httpService.put("user", postData);
      setLoadings({ ...loadings, updateSocialMedia: false });
      if (response.status < 400) {
        setUpdateAccountModal(false);
        toast.success("Edited successfully");
        setSocialMediaAccounts(array);
      }
    } catch ({ err, response }) {
      setLoadings({ ...loadings, updateSocialMedia: false });
    }
  };

  const deleteSocialMedia = async (account) => {
    let array = [...socialMediaAccounts];
    array = array.filter((o) => o.type != account.type);

    let postData = {
      fullname: userProfile.fullname,
      countryId: userProfile?.country?.id,
      expertises: userProfile?.instructor?.expertises?.map((item) => {
        return item.id;
      }),
      bio: userProfile?.instructor?.bio,
      mobile: userProfile?.mobile,
      email: userProfile?.email,
      gender: userProfile.gender,
      address: userProfile?.address,
      meta: {
        social_media_links: array,
      },
    };
    try {
      setLoadings({ ...loadings, deleteSocialMedia: true });
      const response = await httpService.put("user", postData);
      setLoadings({ ...loadings, deleteSocialMedia: false });
      if (response.status < 400) {
        setDeleteAccountModal(false);
        toast.success("Deleted successfully");
        setSocialMediaAccounts(array);
      }
    } catch ({ err, response }) {
      setLoadings({ ...loadings, deleteSocialMedia: false });
    }
  };

  useEffect(() => {
    let array = [];
    if (userProfile && countries?.length > 0 && categories?.length > 0) {
      updateSettingController.setFieldValue("fullname", userProfile?.fullname);
      updateSettingController.setFieldValue(
        "countryId",
        userProfile?.country?.id,
      );
      userProfile?.instructor?.expertises?.map((item) => {
        array.push(item.id);
      });
      updateSettingController.setFieldValue("expertises", array);
      updateSettingController.setFieldValue(
        "bio",
        userProfile?.instructor?.bio,
      );
      updateSettingController.setFieldValue("mobile", userProfile?.mobile);
      updateSettingController.setFieldValue("email", userProfile?.email);
      updateSettingController.setFieldValue("gender", userProfile.gender);
      updateSettingController.setFieldValue("address", userProfile?.address);
    }
  }, [userProfile, countries, categories]);

  useEffect(() => {
    if (
      userProfile &&
      userProfile?.instructor?.meta?.social_media_links?.length > 0
    ) {
      setSocialMediaAccounts(userProfile?.instructor?.meta?.social_media_links);
    } else {
      setSocialMediaAccounts([]);
    }
  }, [userProfile]);

  const exports = {
    // entity methods
    getCountries,
    getCategories,
    updateAvatar,
    deleteSocialMedia,
    // controller methods
    updateSettingController,
    updatePasswordController,
    addSocialMediaController,
    updateSocialMediaController,
    // entity states
    countries,
    categories,
    socialMediaAccounts,
    // controller states
    newAccountModal,
    setNewAccountModal,
    updateAccountModal,
    setUpdateAccountModal,
    deleteAccountModal,
    setDeleteAccountModal,
    selectedSocialMedia,
    setSelectedSocialMedia,
    // loading states
    loadings,
    getCountriesLoading,
    getCategoriesLoading,
  };
  return exports;
};

export default useSetting;
